"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";

import { getTelemetry, getTelemetryAggregates } from "@/lib/api/endpoints/telemetry";
import type { TelemetryAggregatePointDto, TelemetryAggregatesResponseDto, TelemetryDto } from "@/lib/api/types";

import {
  bucketSecondsForRange,
  clampRefreshIntervalMs,
  rangeSelectionToDurationMs,
  timeRangeToDurationMs,
  type DashboardTimeRangeSelection,
  type AggregationKey,
  type TimeRangeKey,
} from "@/components/dashboard/widget-config";

export type UseWidgetDataParams = {
  deviceId: string;
  channel: string;
  timeRange: TimeRangeKey;
  customRange?: DashboardTimeRangeSelection | null;
  aggregation: AggregationKey;
  enabled?: boolean;
  /** When true, skips per-widget fetch (dashboard batch supplies data). */
  suspendFetch?: boolean;
  /** When true, bypasses dashboard batch data for local widget overrides. */
  preferSingleFetch?: boolean;
  /** Polling interval in seconds (10–120); default 30. */
  refreshIntervalSeconds?: number;
};

export type WidgetSeriesNormalized = {
  points: TelemetryAggregatePointDto[];
  bucketSeconds: number;
  metrics: string[];
  channel: string;
  /** Same shape as API; aggregation selection is reserved for future backend support. */
  aggregation: AggregationKey;
};

export type UseWidgetDataResult = {
  data: WidgetSeriesNormalized | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  /** Bumps when a successful fetch completes (stable signal for chart revision). */
  dataUpdatedAt: number;
};

export function normalizeAggregates(
  res: TelemetryAggregatesResponseDto,
  channel: string,
  aggregation: AggregationKey,
): WidgetSeriesNormalized {
  void aggregation;
  return {
    points: res.points,
    bucketSeconds: res.bucket_seconds,
    metrics: res.metrics.length ? res.metrics : [channel],
    channel,
    aggregation,
  };
}

/**
 * Fetches bucketed telemetry for a single device + channel.
 * Uses React Query cache + refetchInterval polling (PART 6).
 */
export function useWidgetData(params: UseWidgetDataParams): UseWidgetDataResult {
  const {
    deviceId,
    channel,
    timeRange,
    customRange,
    aggregation,
    enabled = true,
    suspendFetch = false,
    refreshIntervalSeconds,
  } = params;

  const durationMs = useMemo(
    () => (customRange ? rangeSelectionToDurationMs(customRange) : timeRangeToDurationMs(timeRange)),
    [customRange, timeRange],
  );
  const bucketSeconds = useMemo(() => bucketSecondsForRange(durationMs), [durationMs]);
  const refetchInterval = useMemo(
    () => clampRefreshIntervalMs(refreshIntervalSeconds),
    [refreshIntervalSeconds],
  );

  const query = useQuery({
    queryKey: ["widget-data", deviceId, channel, timeRange, customRange, aggregation, bucketSeconds],
    queryFn: async (): Promise<WidgetSeriesNormalized> => {
      const end = customRange?.key === "custom" ? new Date(customRange.end) : new Date();
      const start = customRange?.key === "custom" ? new Date(customRange.start) : new Date(end.getTime() - durationMs);
      const res = await getTelemetryAggregates({
        deviceId,
        start,
        end,
        bucketSeconds,
        maxPoints: 4096,
        metrics: channel,
      });
      return normalizeAggregates(res, channel, aggregation);
    },
    enabled: enabled && Boolean(deviceId && channel) && !suspendFetch,
    staleTime: Math.min(refetchInterval, 60_000),
    refetchInterval: refetchInterval,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}

/** Debounced manual refresh for all widget series + sample queries (toolbar). */
export function useDebouncedWidgetRefresh(debounceMs = 400) {
  const qc = useQueryClient();
  const timerRef = useRef<number | null>(null);
  return useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void qc.invalidateQueries({ queryKey: ["widget-data"] });
      void qc.invalidateQueries({ queryKey: ["widget-samples"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-telemetry-batch"] });
    }, debounceMs);
  }, [qc, debounceMs]);
}

export type UseWidgetSamplesResult = {
  rows: TelemetryDto[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
};

/** Raw telemetry rows for table widgets (bounded window). */
export function useWidgetSamples(params: UseWidgetDataParams): UseWidgetSamplesResult {
  const { deviceId, channel, timeRange, customRange, enabled = true, refreshIntervalSeconds } = params;
  const durationMs = useMemo(
    () => (customRange ? rangeSelectionToDurationMs(customRange) : timeRangeToDurationMs(timeRange)),
    [customRange, timeRange],
  );
  const refetchInterval = useMemo(
    () => clampRefreshIntervalMs(refreshIntervalSeconds),
    [refreshIntervalSeconds],
  );

  const query = useQuery({
    queryKey: ["widget-samples", deviceId, channel, timeRange, customRange],
    queryFn: async () => {
      const end = customRange?.key === "custom" ? new Date(customRange.end) : new Date();
      const start = customRange?.key === "custom" ? new Date(customRange.start) : new Date(end.getTime() - durationMs);
      const rows = await getTelemetry(deviceId, { start, end });
      return rows.slice(-40);
    },
    enabled: enabled && Boolean(deviceId && channel),
    staleTime: Math.min(refetchInterval, 60_000),
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null,
    refetch: query.refetch,
  };
}
